import React, { useState, useRef } from "react";
import axios from "axios";
import { 
  CheckCircle2, 
  AlertTriangle, 
  XCircle,
  ShieldCheck,
  Loader2,
  ChevronDown,
  ChevronUp,
  FileCheck,
  Calendar,
  Building2,
  FileText,
  Award,
  Users,
  MapPin,
  DollarSign,
  Briefcase,
  Target,
  Clock,
  Upload,
  Brain,
  Sparkles,
  X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProposalSanityChecker = ({ rfpId, rfpName }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({ errors: true, warnings: true, ai: true });
  const [proposalFile, setProposalFile] = useState(null);
  const [proposalContent, setProposalContent] = useState("");
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload a PDF, Word, or text file");
        return;
      }
      if (file.size > 20 * 1024 * 1024) { // 20MB limit
        toast.error("File size must be less than 20MB");
        return;
      }
      setProposalFile(file);
      setProposalContent(""); // Clear text if file is selected
      toast.success(`File selected: ${file.name}`);
    }
  };

  const clearFile = () => {
    setProposalFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const runSanityCheck = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      
      if (proposalFile) {
        formData.append('proposal_file', proposalFile);
      }
      if (proposalContent) {
        formData.append('proposal_content', proposalContent);
      }

      const res = await axios.post(`${API}/rfp/${rfpId}/sanity-check`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(res.data);
      
      if (res.data.status === "pass") {
        toast.success("Proposal ready for submission!");
      } else if (res.data.status === "warning") {
        toast.warning("Proposal has warnings to review");
      } else {
        toast.error("Proposal has critical issues to fix");
      }
    } catch (error) {
      console.error("Error running sanity check:", error);
      toast.error("Failed to run sanity check");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pass":
        return <CheckCircle2 className="w-8 h-8 text-emerald-600" />;
      case "warning":
        return <AlertTriangle className="w-8 h-8 text-amber-600" />;
      case "fail":
        return <XCircle className="w-8 h-8 text-red-600" />;
      default:
        return <ShieldCheck className="w-8 h-8 text-[#64748B]" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pass":
        return "bg-emerald-100 text-emerald-700";
      case "warning":
        return "bg-amber-100 text-amber-700";
      case "fail":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getCategoryIcon = (category) => {
    switch (category?.toLowerCase()) {
      case "mandatory requirements":
      case "compliance":
        return <FileCheck className="w-4 h-4" />;
      case "deadlines":
      case "dates":
        return <Calendar className="w-4 h-4" />;
      case "company profile":
      case "profile":
        return <Building2 className="w-4 h-4" />;
      case "section analysis":
      case "sections":
        return <FileText className="w-4 h-4" />;
      case "certifications":
        return <Award className="w-4 h-4" />;
      case "team resources":
      case "team":
        return <Users className="w-4 h-4" />;
      case "geographic presence":
      case "location":
        return <MapPin className="w-4 h-4" />;
      case "emd/security deposit":
      case "financial":
        return <DollarSign className="w-4 h-4" />;
      case "technical sections":
      case "technical":
        return <Briefcase className="w-4 h-4" />;
      case "bid eligibility":
      case "eligibility":
        return <Target className="w-4 h-4" />;
      case "past performance":
      case "performance":
        return <Clock className="w-4 h-4" />;
      case "content coverage":
      case "content analysis":
      case "rfp terminology":
        return <Brain className="w-4 h-4" />;
      case "ai analysis":
        return <Sparkles className="w-4 h-4" />;
      default:
        return <ShieldCheck className="w-4 h-4" />;
    }
  };

  const toggleSection = (section) => {
    setExpanded(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (!result) {
    return (
      <Card className="border-[#E2E8F0]" data-testid="sanity-checker">
        <CardHeader>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#007AFF]" />
            <CardTitle className="text-base font-bold">Proposal Sanity Checker</CardTitle>
          </div>
          <CardDescription>
            Comprehensive validation of your proposal against 12+ RFP criteria including AI-powered content analysis
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Optional Proposal Upload for AI Analysis */}
          <div className="border-2 border-dashed border-[#E2E8F0] rounded-lg p-4 hover:border-[#007AFF] transition-colors">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-[#007AFF]" />
                <span className="font-medium text-sm">AI Content Analysis (Optional)</span>
              </div>
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600">
                <Sparkles className="w-3 h-3 mr-1" />
                Enhanced
              </Badge>
            </div>
            
            <p className="text-xs text-[#64748B] mb-3">
              Upload your proposal document or paste content for AI-powered gap analysis against RFP requirements
            </p>

            {/* File Upload */}
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="proposal-file-input"
              />
              
              {proposalFile ? (
                <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-700 font-medium truncate max-w-[200px]">
                      {proposalFile.name}
                    </span>
                    <span className="text-xs text-blue-500">
                      ({(proposalFile.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearFile}
                    className="h-6 w-6 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full justify-center"
                  data-testid="upload-proposal-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Proposal (PDF, Word, TXT)
                </Button>
              )}
              
              {/* OR Divider */}
              <div className="flex items-center gap-2">
                <div className="flex-1 border-t border-[#E2E8F0]"></div>
                <span className="text-xs text-[#64748B]">OR</span>
                <div className="flex-1 border-t border-[#E2E8F0]"></div>
              </div>

              {/* Text Input */}
              <Textarea
                placeholder="Paste your proposal content here for AI analysis..."
                value={proposalContent}
                onChange={(e) => {
                  setProposalContent(e.target.value);
                  if (e.target.value) setProposalFile(null);
                }}
                className="min-h-[100px] text-sm"
                data-testid="proposal-content-input"
              />
            </div>
          </div>

          <Button 
            onClick={runSanityCheck} 
            disabled={loading}
            className="w-full bg-[#007AFF] hover:bg-[#0066DD]"
            data-testid="run-sanity-check-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Running Comprehensive Analysis...
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4 mr-2" />
                Run {proposalFile || proposalContent ? 'AI-Enhanced ' : ''}Sanity Check
              </>
            )}
          </Button>
          
          <p className="text-xs text-center text-[#94A3B8]">
            {proposalFile || proposalContent 
              ? "Your proposal will be analyzed against RFP requirements using AI" 
              : "Without proposal content, only RFP metadata and company profile will be checked"}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-[#E2E8F0]" data-testid="sanity-checker-result">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getStatusIcon(result.status)}
            <div>
              <CardTitle className="text-base font-bold">Sanity Check Results</CardTitle>
              <CardDescription>{result.status_message}</CardDescription>
            </div>
          </div>
          <Badge className={`${getStatusColor(result.status)} px-3 py-1`}>
            {result.status === "pass" ? "Ready to Submit" : 
             result.status === "warning" ? "Review Warnings" : "Critical Issues"}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Overall Score */}
        <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-[#F8FAFC] to-[#F1F5F9] rounded-lg">
          <div className={`text-4xl font-extrabold ${
            result.pass_rate >= 80 ? "text-emerald-600" :
            result.pass_rate >= 50 ? "text-amber-600" : "text-red-600"
          }`}>
            {result.pass_rate}%
          </div>
          <div className="flex-1">
            <Progress 
              value={result.pass_rate} 
              className="h-3"
            />
            <div className="flex justify-between mt-2 text-xs text-[#64748B]">
              <span className="text-emerald-600 font-medium">{result.summary?.passed || 0} Passed</span>
              <span className="text-amber-600 font-medium">{result.summary?.warnings || 0} Warnings</span>
              <span className="text-red-600 font-medium">{result.summary?.errors || 0} Errors</span>
            </div>
          </div>
        </div>

        {/* Bid Score Summary */}
        {result.bid_score > 0 && (
          <div className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-lg">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-[#007AFF]" />
              <span className="text-sm font-medium">Bid Matrix Score:</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-lg font-bold ${
                result.bid_score >= 70 ? "text-emerald-600" :
                result.bid_score >= 50 ? "text-amber-600" : "text-red-600"
              }`}>{result.bid_score}%</span>
              <Badge variant="outline" className="text-xs">{result.recommendation}</Badge>
            </div>
          </div>
        )}

        {/* Errors Section */}
        {result.errors?.length > 0 && (
          <div className="border border-red-200 rounded-lg overflow-hidden" data-testid="sanity-errors">
            <button
              onClick={() => toggleSection("errors")}
              className="w-full p-3 bg-red-50 flex items-center justify-between hover:bg-red-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" />
                <span className="font-semibold text-red-700">
                  {result.errors.length} Critical Issue{result.errors.length > 1 ? 's' : ''} - Must Fix Before Submission
                </span>
              </div>
              {expanded.errors ? <ChevronUp className="w-5 h-5 text-red-600" /> : <ChevronDown className="w-5 h-5 text-red-600" />}
            </button>
            {expanded.errors && (
              <div className="p-3 space-y-3">
                {result.errors.map((error, idx) => (
                  <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-100">
                    <div className="flex items-start gap-2 mb-2">
                      {getCategoryIcon(error.category)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-red-800 uppercase tracking-wide">{error.category}</span>
                        </div>
                        <span className="text-sm font-medium text-red-700">{error.message}</span>
                      </div>
                    </div>
                    {error.items?.length > 0 && (
                      <ul className="ml-6 text-xs text-red-600 space-y-1 mb-2">
                        {error.items.map((item, i) => (
                          <li key={i} className="flex items-start gap-1">
                            <span className="text-red-400 mt-0.5">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {error.recommendation && (
                      <div className="mt-2 p-2 bg-white rounded border border-red-100">
                        <span className="text-xs text-red-600"><strong>Fix:</strong> {error.recommendation}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Warnings Section */}
        {result.warnings?.length > 0 && (
          <div className="border border-amber-200 rounded-lg overflow-hidden" data-testid="sanity-warnings">
            <button
              onClick={() => toggleSection("warnings")}
              className="w-full p-3 bg-amber-50 flex items-center justify-between hover:bg-amber-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="font-semibold text-amber-700">
                  {result.warnings.length} Warning{result.warnings.length > 1 ? 's' : ''} - Should Review
                </span>
              </div>
              {expanded.warnings ? <ChevronUp className="w-5 h-5 text-amber-600" /> : <ChevronDown className="w-5 h-5 text-amber-600" />}
            </button>
            {expanded.warnings && (
              <div className="p-3 space-y-3">
                {result.warnings.map((warning, idx) => (
                  <div key={idx} className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <div className="flex items-start gap-2 mb-2">
                      {getCategoryIcon(warning.category)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-amber-800 uppercase tracking-wide">{warning.category}</span>
                        </div>
                        <span className="text-sm font-medium text-amber-700">{warning.message}</span>
                      </div>
                    </div>
                    {warning.items?.length > 0 && (
                      <ul className="ml-6 text-xs text-amber-600 space-y-1 mb-2">
                        {warning.items.map((item, i) => (
                          <li key={i} className="flex items-start gap-1">
                            <span className="text-amber-400 mt-0.5">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {warning.recommendation && (
                      <div className="mt-2 p-2 bg-white rounded border border-amber-100">
                        <span className="text-xs text-amber-600"><strong>Suggestion:</strong> {warning.recommendation}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Passed Checks */}
        {result.checks?.length > 0 && (
          <div className="border border-emerald-200 rounded-lg overflow-hidden" data-testid="sanity-passed">
            <button
              onClick={() => toggleSection("passed")}
              className="w-full p-3 bg-emerald-50 flex items-center justify-between hover:bg-emerald-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <span className="font-semibold text-emerald-700">
                  {result.checks.length} Check{result.checks.length > 1 ? 's' : ''} Passed
                </span>
              </div>
              {expanded.passed ? <ChevronUp className="w-5 h-5 text-emerald-600" /> : <ChevronDown className="w-5 h-5 text-emerald-600" />}
            </button>
            {expanded.passed && (
              <div className="p-3 space-y-2">
                {result.checks.map((check, idx) => (
                  <div key={idx} className="p-2 bg-emerald-50 rounded-lg border border-emerald-100 flex items-center gap-2">
                    {getCategoryIcon(check.category)}
                    <div className="flex-1">
                      <span className="text-xs font-bold text-emerald-800 uppercase tracking-wide mr-2">{check.category}</span>
                      <span className="text-sm text-emerald-700">{check.message}</span>
                    </div>
                    {check.items && check.status === "info" && (
                      <Badge variant="outline" className="text-xs text-emerald-600">{check.items.length} items</Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Document Checklist (from info checks) */}
        {result.checks?.find(c => c.type === "documents") && (
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-blue-700">Required Documents Checklist</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {result.checks.find(c => c.type === "documents")?.items?.map((doc, idx) => (
                <Badge key={idx} variant="outline" className="text-xs text-blue-600 bg-white">
                  {doc}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* AI Analysis Results */}
        {result.ai_analysis && (
          <div className="border border-purple-200 rounded-lg overflow-hidden" data-testid="ai-analysis-results">
            <button
              onClick={() => toggleSection("ai")}
              className="w-full p-3 bg-gradient-to-r from-purple-50 to-blue-50 flex items-center justify-between hover:from-purple-100 hover:to-blue-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-600" />
                <span className="font-semibold text-purple-700">AI Content Analysis</span>
                <Badge className="bg-purple-100 text-purple-700 text-xs">
                  <Sparkles className="w-3 h-3 mr-1" />
                  Powered by Gemini
                </Badge>
              </div>
              {expanded.ai ? <ChevronUp className="w-5 h-5 text-purple-600" /> : <ChevronDown className="w-5 h-5 text-purple-600" />}
            </button>
            {expanded.ai && (
              <div className="p-4 space-y-4 bg-white">
                {/* Scores */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className={`text-2xl font-bold ${
                      result.ai_analysis.content_coverage_score >= 70 ? 'text-emerald-600' :
                      result.ai_analysis.content_coverage_score >= 40 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {result.ai_analysis.content_coverage_score || 0}%
                    </div>
                    <div className="text-xs text-purple-600 mt-1">Content Coverage</div>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className={`text-2xl font-bold ${
                      result.ai_analysis.terminology_alignment >= 70 ? 'text-emerald-600' :
                      result.ai_analysis.terminology_alignment >= 40 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {result.ai_analysis.terminology_alignment || 0}%
                    </div>
                    <div className="text-xs text-blue-600 mt-1">Terminology Match</div>
                  </div>
                  <div className="text-center p-3 bg-indigo-50 rounded-lg">
                    <div className={`text-2xl font-bold ${
                      result.ai_analysis.completeness_score >= 70 ? 'text-emerald-600' :
                      result.ai_analysis.completeness_score >= 40 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {result.ai_analysis.completeness_score || 0}%
                    </div>
                    <div className="text-xs text-indigo-600 mt-1">Completeness</div>
                  </div>
                </div>

                {/* Requirement Gaps */}
                {result.ai_analysis.requirement_gaps?.length > 0 && (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      <span className="text-sm font-semibold text-red-700">Missing Requirements</span>
                    </div>
                    <ul className="space-y-1">
                      {result.ai_analysis.requirement_gaps.slice(0, 5).map((gap, idx) => (
                        <li key={idx} className="text-xs text-red-600 flex items-start gap-1">
                          <span className="text-red-400 mt-0.5">•</span>
                          <span>{gap}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Strong Points */}
                {result.ai_analysis.strong_points?.length > 0 && (
                  <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-semibold text-emerald-700">Strong Points</span>
                    </div>
                    <ul className="space-y-1">
                      {result.ai_analysis.strong_points.slice(0, 5).map((point, idx) => (
                        <li key={idx} className="text-xs text-emerald-600 flex items-start gap-1">
                          <span className="text-emerald-400 mt-0.5">•</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Weak Points */}
                {result.ai_analysis.weak_points?.length > 0 && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-semibold text-amber-700">Areas for Improvement</span>
                    </div>
                    <ul className="space-y-1">
                      {result.ai_analysis.weak_points.slice(0, 5).map((point, idx) => (
                        <li key={idx} className="text-xs text-amber-600 flex items-start gap-1">
                          <span className="text-amber-400 mt-0.5">•</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* AI Recommendations */}
                {result.ai_analysis.recommendations?.length > 0 && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-semibold text-blue-700">AI Recommendations</span>
                    </div>
                    <ul className="space-y-1">
                      {result.ai_analysis.recommendations.slice(0, 5).map((rec, idx) => (
                        <li key={idx} className="text-xs text-blue-600 flex items-start gap-1">
                          <span className="text-blue-400 mt-0.5">{idx + 1}.</span>
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Re-run Button */}
        <Button 
          variant="outline" 
          onClick={runSanityCheck} 
          disabled={loading}
          className="w-full"
          data-testid="rerun-sanity-check-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Re-checking...
            </>
          ) : (
            <>
              <ShieldCheck className="w-4 h-4 mr-2" />
              Re-run Sanity Check
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ProposalSanityChecker;
